import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import UserVerificationModal from '../components/UserVerificationModal';
import { 
  getAllUsers, 
  updateUserAccount, 
  updateTransaction,
  createNewUserWithAccounts,
  subscribeToUserUpdates,
  updateUserStatus,
  rejectUserRegistration,
  requestAdditionalInfo,
  addAdminTransaction,
  findUserByEmail,
  deleteAllUserTransactions,
  deleteSpecificTransaction,
  subscribeToTransactions,
  updateUserShowMockTransactions,
  deleteUserData,
  updateUserName,
  updateUserTransferLimit
} from '../utils/firestore';
import { VALID_OTPS, USED_OTPS, addOTP, removeOTP, resetUsedOTPs } from '../utils/otpValidation';
import { useNavigate } from 'react-router-dom';
import { validateUSAccountNumber, validateUSRoutingNumber } from '../utils/validation';

const otpStyles = {
  section: {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  addSection: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem'
  },
  input: {
    padding: '0.5rem',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    width: '200px'
  },
  button: {
    padding: '0.5rem 1rem',
    backgroundColor: '#0d6efd',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  removeButton: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem'
  },
  otpItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem',
    backgroundColor: 'white',
    borderRadius: '4px',
    marginBottom: '0.5rem'
  },
  otpLists: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem'
  },
  otpListContainer: {
    backgroundColor: 'white',
    padding: '1rem',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  }
};

export default function AdminDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingAccount, setEditingAccount] = useState(null);
  const [showAdminGuide, setShowAdminGuide] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [activeUserTab, setActiveUserTab] = useState('accounts'); // 'accounts' | 'add' | 'transactions'
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [transferLimitOverrideEnabled, setTransferLimitOverrideEnabled] = useState(false);
  const [transferLimitEnabledForUser, setTransferLimitEnabledForUser] = useState(true);
  const [transferLimitAmountForUser, setTransferLimitAmountForUser] = useState(2);
  const [transferLimitSaving, setTransferLimitSaving] = useState(false);
  const [transferLimitFeedback, setTransferLimitFeedback] = useState('');
  const [isEditingHolderName, setIsEditingHolderName] = useState(false);
  const [holderNameDraft, setHolderNameDraft] = useState('');
  const [holderNameSaving, setHolderNameSaving] = useState(false);

  const FEEDBACK_AUTO_CLEAR_MS = 5000;
  const showSuccess = (msg) => {
    setError('');
    setSuccessMessage(msg);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {}
    setTimeout(() => setSuccessMessage(''), FEEDBACK_AUTO_CLEAR_MS);
  };
  const showError = (msg) => {
    setSuccessMessage('');
    setError(msg);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {}
    setTimeout(() => setError(''), FEEDBACK_AUTO_CLEAR_MS);
  };
  const clearFeedback = () => {
    setError('');
    setSuccessMessage('');
  };
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [newOtp, setNewOtp] = useState('');
  const [validOtps, setValidOtps] = useState([]);
  const [usedOtps, setUsedOtps] = useState([]);
  const [newUserData, setNewUserData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    address: {
      street: '',
      city: '',
      county: '',
      postCode: ''
    },
    role: 'user',
    accounts: [
      { type: 'Checking', balance: 0, available: 0 },
      { type: 'Savings', balance: 0, available: 0 }
    ]
  });
  const [newTransactions, setNewTransactions] = useState([{
    accountType: '',
    type: 'debit',
    amount: '',
    description: '',
    category: '',
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
    status: 'Completed',
    updateBalance: true
  }]);
  const [emailToDelete, setEmailToDelete] = useState('');
  const [deleteStatus, setDeleteStatus] = useState('');
  const [userTransactions, setUserTransactions] = useState([]);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deleteUserConfirmText, setDeleteUserConfirmText] = useState('');
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const usersData = await getAllUsers();
        setUsers(usersData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load users');
        setLoading(false);
      }
    };

    loadInitialData();

    const unsubscribe = subscribeToUserUpdates((updatedUsers) => {
      setUsers(updatedUsers);
      if (selectedUser) {
        const updatedUser = updatedUsers.find(u => u.uid === selectedUser.uid);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    });

    return () => unsubscribe();
  }, [selectedUser?.uid]);

  useEffect(() => {
    if (!selectedUser) return;
    const override = selectedUser.transferLimit;
    if (override && typeof override === 'object') {
      setTransferLimitOverrideEnabled(true);
      setTransferLimitEnabledForUser(override.isEnabled !== false);
      setTransferLimitAmountForUser(Number(override.transactionLimit ?? 2) || 2);
    } else {
      setTransferLimitOverrideEnabled(false);
      setTransferLimitEnabledForUser(true);
      setTransferLimitAmountForUser(2);
    }
    setTransferLimitFeedback('');
    setIsEditingHolderName(false);
    setHolderNameDraft(selectedUser.name || '');
  }, [selectedUser?.uid, selectedUser?.transferLimit]);

  useEffect(() => {
    if (selectedUser?.uid) {
      const unsubscribe = subscribeToTransactions(selectedUser.uid, (transactions) => {
        setUserTransactions(transactions);
      });
      return () => unsubscribe();
    }
  }, [selectedUser]);

  useEffect(() => {
    // Update OTP lists whenever they change
    setValidOtps(Array.from(VALID_OTPS));
    setUsedOtps(Array.from(USED_OTPS));
  }, [showOtpSection, showOtpModal]);

  const handleEditAccount = (account) => {
    setEditingAccount({
      id: account.id,
      type: account.type,
      available: parseFloat(account.available || 0),
      balance: parseFloat(account.balance || 0),
      accountNumber: String(account.accountNumber ?? '').replace(/\D/g, ''),
      routingNumber: String(account.routingNumber || account.sortCode || '').replace(/\D/g, '')
    });
  };

  const handleAccountUpdate = async (accountId, updates) => {
    try {
      clearFeedback();
      const accountNumber = String(updates.accountNumber ?? '').replace(/\D/g, '').slice(0, 17);
      const routingNumber = String((updates.routingNumber || updates.sortCode) ?? '')
        .replace(/\D/g, '')
        .slice(0, 9);

      await updateUserAccount(selectedUser.uid, accountId, {
        type: updates.type,
        available: parseFloat(updates.available),
        balance: parseFloat(updates.balance),
        accountNumber: accountNumber || undefined,
        routingNumber: routingNumber || undefined
      });

      const index = parseInt(accountId, 10);
      if (!isNaN(index) && selectedUser.accounts && selectedUser.accounts[index] !== undefined) {
        setSelectedUser(prev => ({
          ...prev,
          accounts: prev.accounts.map((acc, i) =>
            i === index
              ? {
                  ...acc,
                  type: updates.type || acc.type,
                  accountNumber,
                  routingNumber,
                  available: parseFloat(updates.available),
                  balance: parseFloat(updates.balance)
                }
              : acc
          )
        }));
      }
      setEditingAccount(null);
      const isInvestment = String(updates.type || '').toLowerCase() === 'investment';
      showSuccess(
        isInvestment
          ? 'Account updated. Investment return history has been generated from the available balance.'
          : 'Account updated successfully.'
      );
    } catch (err) {
      showError('Failed to update account.');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    clearFeedback();
    setLoading(true);

    try {
      await createNewUserWithAccounts(newUserData);
      setIsCreatingUser(false);
      showSuccess('User created successfully.');
      setNewUserData({
        email: '',
        password: '',
        name: '',
        phone: '',
        address: {
          street: '',
          city: '',
          county: '',
          postCode: ''
        },
        role: 'user',
        accounts: [
          { type: 'Checking', balance: 0, available: 0 },
          { type: 'Savings', balance: 0, available: 0 }
        ]
      });
    } catch (err) {
      showError('Failed to create user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusUpdate = async (userId, newStatus) => {
    try {
      clearFeedback();
      await updateUserStatus(userId, newStatus);
      setShowVerificationModal(false);
      showSuccess('User status updated successfully.');
    } catch (err) {
      showError('Failed to update user status.');
    }
  };

  const handleRejectRegistration = async (userId, reason) => {
    try {
      clearFeedback();
      await rejectUserRegistration(userId, reason);
      setShowVerificationModal(false);
      showSuccess('Registration rejected.');
    } catch (err) {
      showError('Failed to reject registration.');
    }
  };

  const handleRequestInfo = async (userId, requestDetails) => {
    try {
      clearFeedback();
      await requestAdditionalInfo(userId, requestDetails);
      setShowVerificationModal(false);
      showSuccess('Information request sent.');
    } catch (err) {
      showError('Failed to send information request.');
    }
  };

  const handleAddTransactionRow = () => {
    setNewTransactions([...newTransactions, {
      accountType: '',
      type: 'debit',
      amount: '',
      description: '',
      category: '',
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().slice(0, 5),
      status: 'Completed',
      updateBalance: true
    }]);
  };

  const handleRemoveTransactionRow = (index) => {
    const updatedTransactions = newTransactions.filter((_, i) => i !== index);
    setNewTransactions(updatedTransactions);
  };

  const handleTransactionChange = (index, field, value) => {
    const updatedTransactions = [...newTransactions];
    updatedTransactions[index] = {
      ...updatedTransactions[index],
      [field]: value
    };
    setNewTransactions(updatedTransactions);
  };

  const handleBulkTransactionSubmit = async (e) => {
    e.preventDefault();
    clearFeedback();
    setLoading(true);

    try {
      // Process each transaction
      for (const transaction of newTransactions) {
        const dateTime = new Date(`${transaction.date}T${transaction.time}`);
      await addAdminTransaction(selectedUser.uid, {
          ...transaction,
        date: dateTime
      });
      }
      
      // Instead of resetting to a single empty transaction, just clear the values of existing transactions
      setNewTransactions(newTransactions.map(() => ({
        accountType: '',
        type: 'debit',
        amount: '',
        description: '',
        category: '',
        date: new Date().toISOString().slice(0, 10),
        time: new Date().toTimeString().slice(0, 5),
        status: 'Completed',
        updateBalance: true
      })));

      showSuccess('Transactions added successfully.');
    } catch (err) {
      showError('Failed to add transactions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const text = await file.text();
        const rows = text.split('\n');
        const headers = rows[0].split(',');
        
        const transactions = rows.slice(1)
          .filter(row => row.trim()) // Skip empty rows
          .map(row => {
            const values = row.split(',');
            const transaction = {
              accountType: values[0] || '',
              type: values[1] || 'debit',
              amount: values[2] || '',
              description: values[3] || '',
              category: values[4] || '',
              date: values[5] || new Date().toISOString().slice(0, 10),
              time: values[6] || new Date().toTimeString().slice(0, 5),
              status: values[7] || 'Completed',
              updateBalance: true
            };
            return transaction;
          });
        
        setNewTransactions(transactions);
        showSuccess('CSV loaded. Review and submit to add transactions.');
    } catch (err) {
        showError('Failed to parse CSV file: ' + err.message);
      }
    }
  };

  const downloadTemplate = () => {
    const headers = ['Account Type,Transaction Type,Amount,Description,Category,Date,Time,Status'];
    const example = ['Checking,debit,100.00,Sample Transaction,Shopping,2024-03-14,14:30,Completed'];
    const csv = [headers, example].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', 'transaction_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDeleteUserTransactions = async () => {
    try {
      const user = await findUserByEmail(emailToDelete);

      if (!user) {
        showError('User not found.');
        return;
      }

      const deletedCount = await deleteAllUserTransactions(user.id);
      setDeleteStatus('');
      setEmailToDelete('');
      showSuccess(`Successfully deleted ${deletedCount} transactions.`);
    } catch (err) {
      console.error('Error deleting transactions:', err);
      showError('Error deleting transactions.');
    }
  };

  const handleTransactionStatusChange = async (transactionId, nextStatus) => {
    if (!selectedUser?.uid || !transactionId) return;
    try {
      clearFeedback();
      await updateTransaction(selectedUser.uid, transactionId, { status: nextStatus });
      setUserTransactions((prev) =>
        prev.map((t) => (t.id === transactionId ? { ...t, status: nextStatus } : t))
      );
      showSuccess(`Transaction marked as ${nextStatus}.`);
    } catch (err) {
      console.error('Error updating transaction status:', err);
      showError('Failed to update transaction status.');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      clearFeedback();
      await deleteSpecificTransaction(selectedUser.uid, transactionId);
      showSuccess('Transaction deleted successfully.');
    } catch (err) {
      console.error('Error deleting transaction:', err);
      showError('Error deleting transaction.');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser?.uid) return;
    clearFeedback();

    const expected = (selectedUser.email || '').trim().toLowerCase();
    const typed = (deleteUserConfirmText || '').trim().toLowerCase();
    if (!expected || typed !== expected) {
      showError('To confirm, type the user email exactly.');
      return;
    }

    if (currentUser?.uid && currentUser.uid === selectedUser.uid) {
      showError('You cannot delete your own admin account.');
      return;
    }

    setIsDeletingUser(true);
    try {
      await deleteUserData(selectedUser.uid);
      showSuccess('User data deleted successfully.');
      setShowDeleteUserModal(false);
      setDeleteUserConfirmText('');
      setSelectedUser(null);
    } catch (e) {
      console.error('Error deleting user:', e);
      showError(e?.message || 'Failed to delete user.');
    } finally {
      setIsDeletingUser(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesStatus = filter === 'all' ? true : user.status === filter;
    const q = (userSearch || '').trim().toLowerCase();
    const matchesSearch = !q
      ? true
      : String(user.email || '').toLowerCase().includes(q) ||
        String(user.name || '').toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format US account number for display (groups of 4)
  const formatAccountNumber = (number) => {
    if (!number) return '';
    const cleaned = String(number).replace(/\D/g, '');
    if (!cleaned) return '';
    return cleaned.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const validateAccountNumber = (number) => validateUSAccountNumber(String(number ?? ''));

  const handleAddOtp = () => {
    if (addOTP(newOtp)) {
      setNewOtp('');
      setValidOtps(Array.from(VALID_OTPS));
    } else {
      alert('Invalid OTP format. Please enter a 6-digit number.');
    }
  };

  const handleRemoveOtp = (otp) => {
    if (removeOTP(otp)) {
      setValidOtps(Array.from(VALID_OTPS));
    }
  };

  const handleResetUsedOtps = () => {
    resetUsedOTPs();
    setUsedOtps([]);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pt-32">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Admin Dashboard</h1>
            <p className="text-sm text-stone-600 mt-1">
              Manage user registrations and accounts
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowAdminGuide(true)}
              className="px-4 py-2 rounded-xl border border-stone-200 bg-white text-stone-800 shadow-sm hover:border-stone-300 hover:bg-stone-50"
            >
              Admin Guide
            </button>
            <button 
              type="button"
              onClick={() => {
                setShowOtpModal(true);
                setShowOtpSection(true);
              }}
              className="px-4 py-2 rounded-xl border border-primary-200 bg-primary-50 text-primary-900 hover:bg-primary-100"
            >
              Manage OTPs
            </button>
            <button 
              onClick={() => setIsCreatingUser(true)}
              className="px-4 py-2 rounded-xl bg-emerald-700 text-white shadow-sm hover:bg-emerald-800"
            >
              Create New User
            </button>
          </div>
        </div>

        {/* Success / Error feedback */}
        {(successMessage || error) && (
          <div
            className={`mb-6 p-4 rounded-xl border flex items-center justify-between gap-4 ${
              successMessage
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            <p className="flex-1 font-medium">
              {successMessage || error}
            </p>
            <button
              type="button"
              onClick={clearFeedback}
              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-black/5 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Filter + search */}
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'pending', label: 'Pending' },
              { key: 'approved', label: 'Approved' },
              { key: 'rejected', label: 'Rejected' },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  filter === t.key
                    ? 'bg-emerald-800 text-white'
                    : 'bg-white text-stone-700 border border-stone-200 hover:bg-stone-50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="w-full md:w-[360px]">
            <label className="block text-sm font-medium text-stone-700 mb-1">Search users</label>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search by email or name…"
              className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-stone-900 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Users List */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200/70 p-4 lg:sticky lg:top-28 h-fit">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-lg font-semibold text-stone-900">Users</h2>
              <span className="text-xs text-stone-500">{filteredUsers.length}</span>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
              {filteredUsers.map(user => (
                <button
                  key={user.uid}
                  onClick={() => {
                    setSelectedUser(user);
                    setActiveUserTab('accounts');
                  }}
                  className={`w-full p-3 rounded-xl text-left transition duration-200 border ${
                    selectedUser?.uid === user.uid
                      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                      : 'bg-white border-stone-200 hover:bg-stone-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-stone-900 truncate">{user.email}</div>
                      <div className="text-xs text-stone-500 mt-1">
                        {(user.accounts?.length || 0)} accounts
                        {user.name ? <span className="text-stone-300"> · </span> : null}
                        {user.name ? <span className="truncate">{user.name}</span> : null}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${
                      user.status === 'approved' ? 'bg-green-100 text-green-800' :
                      user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      user.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.status || 'unknown'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* User Details */}
          <div className="lg:col-span-3">
            {selectedUser ? (
              <div className="space-y-6">
                {/* User Info */}
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200/70 p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-stone-900">
                        User Details: {selectedUser.email}
                      </h2>
                      <div className="mt-3">
                        {!isEditingHolderName ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm text-stone-700">
                              <span className="font-semibold text-stone-900">Account holder:</span>{' '}
                              {selectedUser.name ? selectedUser.name : <span className="text-stone-500">Not set</span>}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setHolderNameDraft(selectedUser.name || '');
                                setIsEditingHolderName(true);
                              }}
                              className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm font-semibold text-stone-800 hover:bg-stone-50"
                            >
                              Edit
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <input
                              type="text"
                              value={holderNameDraft}
                              onChange={(e) => setHolderNameDraft(e.target.value)}
                              placeholder="Full name"
                              className="w-full sm:w-[360px] rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-stone-900 shadow-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={holderNameSaving}
                                onClick={async () => {
                                  try {
                                    setHolderNameSaving(true);
                                    clearFeedback();
                                    const nextName = await updateUserName(selectedUser.uid, holderNameDraft);
                                    setSelectedUser((prev) => (prev ? { ...prev, name: nextName } : prev));
                                    setUsers((prev) =>
                                      prev.map((u) => (u.uid === selectedUser.uid ? { ...u, name: nextName } : u))
                                    );
                                    setIsEditingHolderName(false);
                                    showSuccess('Account holder name updated.');
                                  } catch (err) {
                                    showError(err?.message || 'Failed to update name.');
                                  } finally {
                                    setHolderNameSaving(false);
                                  }
                                }}
                                className="rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60"
                              >
                                {holderNameSaving ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                disabled={holderNameSaving}
                                onClick={() => {
                                  setIsEditingHolderName(false);
                                  setHolderNameDraft(selectedUser.name || '');
                                }}
                                className="rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-800 hover:bg-stone-50 disabled:opacity-60"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-stone-600 mt-1">
                        Created: {formatDate(selectedUser.createdAt)}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setShowVerificationModal(true)}
                        className="px-4 py-2 rounded-xl bg-emerald-800 text-white hover:bg-emerald-900"
                      >
                        Verify User
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteUserConfirmText('');
                          setShowDeleteUserModal(true);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Delete User
                      </button>
                      <button
                        onClick={async () => {
                          const next = selectedUser.showMockTransactions !== false ? false : true;
                          try {
                            await updateUserShowMockTransactions(selectedUser.uid, next);
                            setSelectedUser(prev => prev ? { ...prev, showMockTransactions: next } : null);
                            setUsers(prev => prev.map(u => u.uid === selectedUser.uid ? { ...u, showMockTransactions: next } : u));
                            showSuccess(next ? 'Sample transactions turned on for this user.' : 'Sample transactions turned off for this user.');
                          } catch (e) {
                            showError(e?.message || 'Failed to update setting.');
                          }
                        }}
                        className={`px-4 py-2 rounded-lg border ${selectedUser.showMockTransactions !== false
                          ? 'bg-primary-50 border-primary-200 text-primary-800 hover:bg-primary-100'
                          : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {selectedUser.showMockTransactions !== false ? 'Sample transactions: On' : 'Sample transactions: Off'}
                      </button>
                    </div>
                  </div>

                  {/* Per-user transfer limit override */}
                  <div className="mb-6 rounded-2xl border border-stone-200 bg-stone-50 p-5">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-stone-900">Transfer limit (per user)</h3>
                        <p className="mt-1 text-sm text-stone-600">
                          Override the global transfer limit for this user only.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-sm font-medium text-stone-800">
                          <input
                            type="checkbox"
                            checked={transferLimitOverrideEnabled}
                            onChange={async (e) => {
                              const enabled = e.target.checked;
                              setTransferLimitOverrideEnabled(enabled);
                              try {
                                setTransferLimitSaving(true);
                                setTransferLimitFeedback('');
                                const nextLimit = enabled
                                  ? {
                                      isEnabled: transferLimitEnabledForUser,
                                      transactionLimit: transferLimitAmountForUser,
                                    }
                                  : null;
                                await updateUserTransferLimit(selectedUser.uid, {
                                  overrideEnabled: enabled,
                                  isEnabled: transferLimitEnabledForUser,
                                  transactionLimit: transferLimitAmountForUser,
                                });
                                setSelectedUser((prev) => (prev ? { ...prev, transferLimit: nextLimit } : prev));
                                setUsers((prev) =>
                                  prev.map((u) => (u.uid === selectedUser.uid ? { ...u, transferLimit: nextLimit } : u))
                                );
                                setTransferLimitFeedback(enabled ? 'Override enabled.' : 'Override removed (global applies).');
                                showSuccess(enabled ? 'Per-user transfer limit enabled.' : 'Per-user transfer limit removed (global applies).');
                              } catch (err) {
                                setTransferLimitFeedback('Save failed.');
                                showError(err?.message || 'Failed to update transfer limit.');
                              } finally {
                                setTransferLimitSaving(false);
                              }
                            }}
                            className="h-4 w-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-600/30"
                          />
                          Override
                        </label>

                        <label className="inline-flex items-center gap-2 text-sm font-medium text-stone-800">
                          <span>Limit</span>
                          <select
                            value={transferLimitAmountForUser}
                            onChange={(e) => setTransferLimitAmountForUser(Number(e.target.value))}
                            disabled={!transferLimitOverrideEnabled}
                            className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 shadow-sm disabled:opacity-60"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="inline-flex items-center gap-2 text-sm font-medium text-stone-800">
                          <span>Status</span>
                          <select
                            value={transferLimitEnabledForUser ? 'on' : 'off'}
                            onChange={(e) => setTransferLimitEnabledForUser(e.target.value === 'on')}
                            disabled={!transferLimitOverrideEnabled}
                            className="rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 shadow-sm disabled:opacity-60"
                          >
                            <option value="on">On</option>
                            <option value="off">Off</option>
                          </select>
                        </label>

                        <button
                          type="button"
                          disabled={!transferLimitOverrideEnabled}
                          onClick={async () => {
                            try {
                              setTransferLimitSaving(true);
                              setTransferLimitFeedback('');
                              const nextLimit = {
                                isEnabled: transferLimitEnabledForUser,
                                transactionLimit: transferLimitAmountForUser,
                              };
                              await updateUserTransferLimit(selectedUser.uid, {
                                overrideEnabled: true,
                                isEnabled: transferLimitEnabledForUser,
                                transactionLimit: transferLimitAmountForUser,
                              });
                              setSelectedUser((prev) => (prev ? { ...prev, transferLimit: nextLimit } : prev));
                              setUsers((prev) =>
                                prev.map((u) => (u.uid === selectedUser.uid ? { ...u, transferLimit: nextLimit } : u))
                              );
                              setTransferLimitFeedback('Saved.');
                              showSuccess('Per-user transfer limit saved.');
                            } catch (err) {
                              setTransferLimitFeedback('Save failed.');
                              showError(err?.message || 'Failed to update transfer limit.');
                            } finally {
                              setTransferLimitSaving(false);
                            }
                          }}
                          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-60"
                        >
                          {transferLimitSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                    {transferLimitFeedback ? (
                      <p className="mt-3 text-sm text-stone-600">
                        <span className="font-medium text-stone-800">Transfer limit:</span> {transferLimitFeedback}
                      </p>
                    ) : null}
                  </div>

                  {/* User workspace tabs */}
                  <div className="mb-6 flex flex-wrap gap-2 border-b border-stone-200 pb-4">
                    {[
                      { key: 'accounts', label: 'Accounts' },
                      { key: 'add', label: 'Add transactions' },
                      { key: 'transactions', label: 'Transactions' },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setActiveUserTab(t.key)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                          activeUserTab === t.key
                            ? 'bg-emerald-800 text-white'
                            : 'bg-white border border-stone-200 text-stone-700 hover:bg-stone-50'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Accounts */}
                  {activeUserTab === 'accounts' && (
                    <div className="space-y-4">
                    {selectedUser.accounts?.map(account => (
                      <div key={account.id} 
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        {editingAccount?.id === account.id ? (
                          <form onSubmit={(e) => {
                            e.preventDefault();
                            if (!validateAccountNumber(editingAccount.accountNumber)) {
                              showError('Account number must be 4–17 digits.');
                              return;
                            }
                            if (!validateUSRoutingNumber(String(editingAccount.routingNumber || ''))) {
                              showError('Routing number must be 9 digits with a valid checksum.');
                              return;
                            }
                            handleAccountUpdate(account.id, editingAccount);
                          }}>
                            <div className="grid grid-cols-1 gap-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Account Type
                                  </label>
                                  <select
                                    value={editingAccount.type}
                                    onChange={(e) => setEditingAccount({ ...editingAccount, type: e.target.value })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                  >
                                    <option value="Checking">Checking</option>
                                    <option value="Savings">Savings</option>
                                    <option value="Inheritance">Inheritance</option>
                                    <option value="Investment">Investment</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Available Balance
                                  </label>
                                  <input
                                    type="number"
                                    value={editingAccount.available}
                                    onChange={(e) => setEditingAccount({
                                      ...editingAccount,
                                      available: parseFloat(e.target.value)
                                    })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ledger balance
                                  </label>
                                  <input
                                    type="number"
                                    value={editingAccount.balance}
                                    onChange={(e) => setEditingAccount({
                                      ...editingAccount,
                                      balance: parseFloat(e.target.value)
                                    })}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Account Number
                                  </label>
                                  <input
                                    type="text"
                                    value={formatAccountNumber(editingAccount.accountNumber)}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 17);
                                      setEditingAccount({
                                        ...editingAccount,
                                        accountNumber: cleaned
                                      });
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded-lg font-mono"
                                    placeholder="e.g. 1234 5678 9012"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Typically 4–17 digits
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Routing number
                                  </label>
                                  <input
                                    type="text"
                                    value={editingAccount.routingNumber || ''}
                                    onChange={(e) => {
                                      const cleaned = e.target.value.replace(/\D/g, '').slice(0, 9);
                                      setEditingAccount({
                                        ...editingAccount,
                                        routingNumber: cleaned
                                      });
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded-lg font-mono"
                                    placeholder="021000021"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex justify-end space-x-2 mt-4">
                              <button
                                type="button"
                                onClick={() => setEditingAccount(null)}
                                className="px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-lg"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                              >
                                Save Changes
                              </button>
                            </div>
                          </form>
                        ) : (
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-medium text-gray-900">{account.type}</h3>
                              <button
                                onClick={() => handleEditAccount(account)}
                                className="text-emerald-600 hover:text-emerald-700"
                              >
                                Edit
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-2">
                              <div>
                                <div className="text-sm text-gray-500">Available Balance</div>
                                <div className="font-medium">${(account.available || 0).toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Ledger balance</div>
                                <div className="font-medium">${(account.balance || 0).toFixed(2)}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="text-sm text-gray-500">Account Number</div>
                                <div className="font-mono font-medium">{formatAccountNumber(account.accountNumber)}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Routing number</div>
                                <div className="font-mono font-medium">{account.routingNumber || account.sortCode}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                </div>

                {/* Add Transactions */}
                {activeUserTab === 'add' && (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200/70 p-6 mb-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-medium text-gray-900">Add Transactions</h3>
                    <div className="flex space-x-4">
                      <button
                        onClick={downloadTemplate}
                        className="px-4 py-2 text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50"
                      >
                        Download Template
                      </button>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="csv-upload"
                        />
                        <label
                          htmlFor="csv-upload"
                          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer"
                        >
                          Upload CSV
                        </label>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleBulkTransactionSubmit} className="space-y-4">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {newTransactions.map((transaction, index) => (
                            <tr key={index}>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.accountType}
                                  onChange={(e) => handleTransactionChange(index, 'accountType', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                                  <option value="">Select</option>
                          {selectedUser.accounts.map(account => (
                            <option key={account.id} value={account.type}>{account.type}</option>
                          ))}
                        </select>
                              </td>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.type}
                                  onChange={(e) => handleTransactionChange(index, 'type', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                          <option value="debit">Debit</option>
                          <option value="credit">Credit</option>
                        </select>
                              </td>
                              <td className="px-2 py-2">
                        <input
                          type="number"
                          step="0.01"
                                  value={transaction.amount}
                                  onChange={(e) => handleTransactionChange(index, 'amount', e.target.value)}
                                  className="w-24 p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  value={transaction.description}
                                  onChange={(e) => handleTransactionChange(index, 'description', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                                  required
                                />
                              </td>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.category}
                                  onChange={(e) => handleTransactionChange(index, 'category', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                                  <option value="">Select</option>
                          <option value="Transfer">Transfer</option>
                          <option value="Bill Pay">Bill Pay</option>
                          <option value="Deposit">Deposit</option>
                          <option value="Withdrawal">Withdrawal</option>
                          <option value="Income">Income</option>
                          <option value="Shopping">Shopping</option>
                          <option value="Food">Food</option>
                          <option value="Transportation">Transportation</option>
                          <option value="Entertainment">Entertainment</option>
                          <option value="Other">Other</option>
                        </select>
                              </td>
                              <td className="px-2 py-2">
                          <input
                            type="date"
                                  value={transaction.date}
                                  onChange={(e) => handleTransactionChange(index, 'date', e.target.value)}
                                  className="w-32 p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            required
                          />
                              </td>
                              <td className="px-2 py-2">
                          <input
                            type="time"
                                  value={transaction.time}
                                  onChange={(e) => handleTransactionChange(index, 'time', e.target.value)}
                                  className="w-24 p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                            required
                          />
                              </td>
                              <td className="px-2 py-2">
                        <select
                                  value={transaction.status}
                                  onChange={(e) => handleTransactionChange(index, 'status', e.target.value)}
                                  className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        >
                          <option value="Completed">Completed</option>
                          <option value="Pending">Pending</option>
                          <option value="Failed">Failed</option>
                        </select>
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveTransactionRow(index)}
                                  className="text-red-600 hover:text-red-800"
                                  disabled={newTransactions.length === 1}
                                >
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-between items-center pt-4">
                      <button
                        type="button"
                        onClick={handleAddTransactionRow}
                        className="px-4 py-2 text-emerald-600 hover:text-emerald-700 font-medium flex items-center"
                      >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Row
                      </button>

                      <button
                        type="submit"
                        className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </>
                        ) : (
                          'Submit All Transactions'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
                )}

                {/* User Transactions */}
                {activeUserTab === 'transactions' && (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200/70 p-6 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">User Transactions</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {userTransactions.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatDate(transaction.date)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {transaction.accountType}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {transaction.type}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium">
                              {String(transaction.status || '').toLowerCase() === 'pending' ? (
                                <span className="text-amber-700 font-semibold">Pending</span>
                              ) : (
                                <span className={transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                                  {transaction.type === 'credit' ? '+' : '-'}$
                                  {Math.abs(transaction.amount).toFixed(2)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {transaction.description}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <select
                                value={
                                  String(transaction.status || 'Completed').toLowerCase() === 'pending'
                                    ? 'Pending'
                                    : String(transaction.status || 'Completed').toLowerCase() === 'failed'
                                      ? 'Failed'
                                      : 'Completed'
                                }
                                onChange={(e) => handleTransactionStatusChange(transaction.id, e.target.value)}
                                className={`px-2 py-1 rounded-lg text-xs font-medium border ${
                                  String(transaction.status || '').toLowerCase() === 'pending'
                                    ? 'bg-yellow-50 text-yellow-800 border-yellow-200'
                                    : String(transaction.status || '').toLowerCase() === 'failed'
                                      ? 'bg-red-50 text-red-800 border-red-200'
                                      : 'bg-green-50 text-green-800 border-green-200'
                                }`}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Completed">Completed</option>
                                <option value="Failed">Failed</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => handleDeleteTransaction(transaction.id)}
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {deleteStatus && (
                    <p className={`mt-4 ${deleteStatus.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
                      {deleteStatus}
                    </p>
                  )}
                </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200/70 p-10 text-center">
                <div className="mx-auto max-w-md">
                  <h2 className="text-lg font-semibold text-stone-900">Select a user</h2>
                  <p className="mt-2 text-sm text-stone-600">
                    Choose a user from the list to manage accounts, transactions, verification, and settings.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* OTP Management Modal */}
        {showOtpModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">OTP Management</h2>
                  <p className="mt-1 text-sm text-gray-600">Add, remove, and reset OTP codes.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowOtpModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  aria-label="Close"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="w-full sm:max-w-sm">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Add new OTP</label>
                    <input
                      type="text"
                      value={newOtp}
                      onChange={(e) => setNewOtp(e.target.value)}
                      placeholder="6-digit code"
                      maxLength={6}
                      className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 font-mono text-stone-900 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAddOtp}
                    className="px-5 py-2.5 rounded-xl bg-emerald-700 text-white hover:bg-emerald-800"
                  >
                    Add OTP
                  </button>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
                    <div className="flex items-baseline justify-between">
                      <h3 className="font-semibold text-stone-900">Valid OTPs</h3>
                      <span className="text-xs text-stone-500">{validOtps.length}</span>
                    </div>
                    <div className="mt-4 max-h-[45vh] overflow-auto space-y-2 pr-1">
                      {validOtps.length ? (
                        validOtps.map((otp) => (
                          <div key={otp} className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-4 py-2.5">
                            <span className="font-mono font-semibold text-stone-900">{otp}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveOtp(otp)}
                              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-stone-600">No valid OTPs.</p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-stone-900">Used OTPs</h3>
                        <p className="text-xs text-stone-500">{usedOtps.length} used</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleResetUsedOtps}
                        className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-stone-800 hover:bg-stone-100"
                      >
                        Clear used
                      </button>
                    </div>
                    <div className="mt-4 max-h-[45vh] overflow-auto space-y-2 pr-1">
                      {usedOtps.length ? (
                        usedOtps.map((otp) => (
                          <div key={otp} className="rounded-xl border border-stone-200 bg-white px-4 py-2.5">
                            <span className="font-mono font-semibold text-stone-900">{otp}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-stone-600">No used OTPs.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowOtpModal(false)}
                  className="px-6 py-2.5 rounded-xl bg-emerald-700 text-white hover:bg-emerald-800"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Modal */}
        {showVerificationModal && selectedUser && (
          <UserVerificationModal
            user={selectedUser}
            onClose={() => {
              setShowVerificationModal(false);
            }}
            onApprove={(userId) => handleUserStatusUpdate(userId, 'approved')}
            onReject={handleRejectRegistration}
            onRequestInfo={handleRequestInfo}
          />
        )}

        {/* Admin Guide Modal */}
        {showAdminGuide && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Admin Guide</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Practical step-by-step instructions for setting up users, accounts, and transactions.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdminGuide(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  aria-label="Close"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-8 text-gray-800">
                <section>
                  <h3 className="text-lg font-semibold text-gray-900">1) Understanding the layout</h3>
                  <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-gray-700">
                    <li>
                      <span className="font-semibold">Users (left column)</span>: select a user to view details, accounts, and transactions.
                    </li>
                    <li>
                      <span className="font-semibold">User Details (right)</span>: edit account balances and numbers, toggle sample transactions, verify/approve users, delete users.
                    </li>
                    <li>
                      <span className="font-semibold">Add Transactions</span>: add one or many transactions for the selected user (manual rows or CSV upload).
                    </li>
                    <li>
                      <span className="font-semibold">User Transactions</span>: view and delete specific transactions for the selected user.
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">2) User lifecycle (registration → approval)</h3>
                  <ol className="mt-3 list-decimal pl-5 space-y-2 text-sm text-gray-700">
                    <li>Select a user with status <span className="font-semibold">pending</span>.</li>
                    <li>Click <span className="font-semibold">Verify User</span>.</li>
                    <li>
                      Choose one:
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li><span className="font-semibold">Approve</span>: sets status to approved (user can proceed).</li>
                        <li><span className="font-semibold">Reject</span>: records a reason and moves data to rejected applications.</li>
                        <li><span className="font-semibold">Request info</span>: sets verification to “info requested” and logs the request.</li>
                      </ul>
                    </li>
                  </ol>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">3) Creating a user (admin-created)</h3>
                  <ol className="mt-3 list-decimal pl-5 space-y-2 text-sm text-gray-700">
                    <li>Click <span className="font-semibold">Create New User</span>.</li>
                    <li>Fill in email, password, name, phone, address, and role.</li>
                    <li>
                      Set initial balances for Checking/Savings. When you create the user:
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li>Accounts are created with demo account numbers and routing number.</li>
                        <li>The user’s Firestore profile is created under <code className="px-1 py-0.5 bg-gray-100 rounded">users/&lt;uid&gt;</code>.</li>
                      </ul>
                    </li>
                  </ol>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">4) Setting up accounts (balances + US account/routing)</h3>
                  <p className="mt-3 text-sm text-gray-700">
                    Go to <span className="font-semibold">User Details</span> → <span className="font-semibold">Accounts</span>.
                  </p>
                  <ol className="mt-3 list-decimal pl-5 space-y-2 text-sm text-gray-700">
                    <li>Click <span className="font-semibold">Edit</span> on an account (Checking/Savings).</li>
                    <li>
                      Update:
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li><span className="font-semibold">Available balance</span> and <span className="font-semibold">Ledger balance</span> (numbers).</li>
                        <li><span className="font-semibold">Account number</span>: 4–17 digits (digits only).</li>
                        <li><span className="font-semibold">Routing number</span>: 9 digits, must pass checksum validation.</li>
                      </ul>
                    </li>
                    <li>Click <span className="font-semibold">Save Changes</span>.</li>
                  </ol>
                  <p className="mt-3 text-xs text-gray-500">
                    Tip: Ledger and available should generally match unless you want to simulate holds/pending debits.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">5) Adding transactions (single or bulk)</h3>
                  <p className="mt-3 text-sm text-gray-700">
                    Use <span className="font-semibold">Add Transactions</span> after selecting a user.
                  </p>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="rounded-lg border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900">Manual rows</h4>
                      <ol className="mt-2 list-decimal pl-5 space-y-1 text-sm text-gray-700">
                        <li>Choose account (Checking/Savings).</li>
                        <li>Select type: <span className="font-semibold">debit</span> or <span className="font-semibold">credit</span>.</li>
                        <li>Enter amount, description, category, date, time, and status.</li>
                        <li>Click <span className="font-semibold">Submit All Transactions</span>.</li>
                      </ol>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900">CSV upload</h4>
                      <ol className="mt-2 list-decimal pl-5 space-y-1 text-sm text-gray-700">
                        <li>Click <span className="font-semibold">Download Template</span>.</li>
                        <li>Fill rows in your spreadsheet and export to CSV.</li>
                        <li>Click <span className="font-semibold">Upload CSV</span> and review the loaded rows.</li>
                        <li>Submit to create the transactions.</li>
                      </ol>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Important: If “Update balance” is enabled for those rows, balances will be adjusted as transactions are created.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">6) Deleting transactions</h3>
                  <ol className="mt-3 list-decimal pl-5 space-y-2 text-sm text-gray-700">
                    <li>Select a user.</li>
                    <li>In <span className="font-semibold">User Transactions</span>, click <span className="font-semibold">Delete</span> on a row.</li>
                    <li>The transaction is removed and the user’s account balance is adjusted accordingly.</li>
                  </ol>
                  <p className="mt-3 text-xs text-gray-500">
                    Note: There is also a separate “Delete User Transactions” tool by email near the bottom which deletes all transactions for that user.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">7) Sample transactions toggle</h3>
                  <p className="mt-3 text-sm text-gray-700">
                    In <span className="font-semibold">User Details</span> you can toggle
                    <span className="font-semibold"> Sample transactions: On/Off</span>.
                  </p>
                  <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-gray-700">
                    <li><span className="font-semibold">On</span>: merges mock/sample transactions into the user’s dashboard feed.</li>
                    <li><span className="font-semibold">Off</span>: shows only real transactions stored in Firestore.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">8) OTP management (admin)</h3>
                  <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-gray-700">
                    <li>Click <span className="font-semibold">Manage OTPs</span>.</li>
                    <li>Add a new OTP using a 6-digit code, or remove an existing one.</li>
                    <li>Clear “used OTPs” if you need to re-enable a code for testing.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">9) Deleting a user</h3>
                  <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-gray-700">
                    <li>Select a user, then click <span className="font-semibold">Delete User</span>.</li>
                    <li>Type the user’s email exactly to confirm.</li>
                    <li>
                      This removes Firestore data (profile + related records). It does <span className="font-semibold">not</span> delete the Firebase Auth account.
                      If you need Auth deletion too, add an Admin SDK backend function.
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900">10) Reset transfer counts</h3>
                  <p className="mt-3 text-sm text-gray-700">
                    Use the <span className="font-semibold">Reset Transfer Counts</span> button at the bottom when you need to reset limits/counters.
                  </p>
                </section>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAdminGuide(false)}
                  className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {showDeleteUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Delete user</h2>
                <p className="mt-2 text-sm text-gray-600">
                  This will delete the user&apos;s Firestore profile and related data (including transactions).
                  It does <span className="font-semibold">not</span> delete the Firebase Auth account.
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                  <p className="font-semibold">This action cannot be undone.</p>
                  <p className="mt-1">
                    Type <span className="font-mono font-semibold">{selectedUser.email}</span> to confirm.
                  </p>
                </div>
                <input
                  type="text"
                  value={deleteUserConfirmText}
                  onChange={(e) => setDeleteUserConfirmText(e.target.value)}
                  placeholder="Type user email to confirm"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (isDeletingUser) return;
                    setShowDeleteUserModal(false);
                    setDeleteUserConfirmText('');
                  }}
                  className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  disabled={isDeletingUser}
                  className="px-5 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {isDeletingUser ? 'Deleting…' : 'Delete user'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {isCreatingUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl flex flex-col max-h-[90vh] relative">
              {/* Modal Header - Fixed */}
              <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white rounded-t-xl z-10">
                <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
                <button
                  onClick={() => setIsCreatingUser(false)}
                  className="text-gray-400 hover:text-gray-500 p-2"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                <form id="createUserForm" onSubmit={handleCreateUser} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={newUserData.name}
                        onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        required
                      />
                    </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={newUserData.phone}
                        onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={newUserData.role}
                        onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        required
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                </div>
                
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <input
                          type="text"
                          value={newUserData.address.street}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, street: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={newUserData.address.city}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, city: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          value={newUserData.address.county}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, county: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP code</label>
                        <input
                          type="text"
                          value={newUserData.address.postCode}
                          onChange={(e) => setNewUserData({
                            ...newUserData,
                            address: {...newUserData.address, postCode: e.target.value}
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="e.g. 10001"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Initial Account Balances</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {newUserData.accounts.map((account, index) => (
                        <div key={account.type} className="space-y-4">
                          <h4 className="font-medium text-gray-700">{account.type} Account</h4>
                      <div>
                            <label className="block text-sm text-gray-600 mb-1">Available Balance</label>
                        <input
                          type="number"
                          value={account.available}
                          onChange={(e) => {
                            const newAccounts = [...newUserData.accounts];
                            newAccounts[index] = {
                              ...account,
                              available: parseFloat(e.target.value)
                            };
                            setNewUserData({...newUserData, accounts: newAccounts});
                          }}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div>
                            <label className="block text-sm text-gray-600 mb-1">Ledger balance</label>
                        <input
                          type="number"
                          value={account.balance}
                          onChange={(e) => {
                            const newAccounts = [...newUserData.accounts];
                            newAccounts[index] = {
                              ...account,
                              balance: parseFloat(e.target.value)
                            };
                            setNewUserData({...newUserData, accounts: newAccounts});
                          }}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          step="0.01"
                          min="0"
                        />
                    </div>
                  </div>
                ))}
                    </div>
                  </div>
                </form>
              </div>

              {/* Modal Footer - Fixed */}
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-4 sticky bottom-0 bg-white rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setIsCreatingUser(false)}
                  className="px-6 py-3 text-gray-700 hover:bg-gray-100 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                  form="createUserForm"
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
            </div>
          </div>
        )}

        <div className="mt-10">
          <h2 className="text-lg font-semibold text-stone-900">Admin tools</h2>
          <p className="mt-1 text-sm text-stone-600">Bulk actions and maintenance utilities.</p>

          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-stone-200/70 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-stone-900">Delete user transactions</h3>
              <p className="mt-1 text-sm text-stone-600">
                Remove <span className="font-semibold">all</span> transactions for a user by email.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="email"
                  value={emailToDelete}
                  onChange={(e) => setEmailToDelete(e.target.value)}
                  placeholder="user@email.com"
                  className="flex-1 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-stone-900 shadow-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20"
                />
                <button
                  type="button"
                  onClick={handleDeleteUserTransactions}
                  className="rounded-xl bg-red-600 px-5 py-2.5 font-semibold text-white hover:bg-red-700"
                >
                  Delete all
                </button>
              </div>
              {deleteStatus && (
                <p className={`mt-3 text-sm ${deleteStatus.includes('Error') ? 'text-red-600' : 'text-emerald-700'}`}>
                  {deleteStatus}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-stone-200/70 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-stone-900">Reset transfer counts</h3>
              <p className="mt-1 text-sm text-stone-600">
                Use when you need to reset transfer counters/limits for testing or support.
              </p>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin/reset-transfers')}
                  className="rounded-xl bg-emerald-700 px-5 py-2.5 font-semibold text-white hover:bg-emerald-800"
                >
                  Go to reset page
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
} 